// Removed Credentials import - using backend auth now
import { fetchSearchConsoleData, formatDateForAPI } from './searchConsole';

export interface HistoricalPosition {
  current: number | null;
  month1: number | null;
  month2: number | null;
  month3: number | null;
  month4: number | null;
  month5: number | null;
  month6: number | null;
}

export interface HistoricalRankingData {
  query: string;
  positions: HistoricalPosition;
  trend: 'up' | 'down' | 'stable' | 'new';
  changeFromPrevious: number | null;
}

export interface HistoricalRankingsResult {
  data: HistoricalRankingData[];
  loading: boolean;
  periods: {
    current: { start: string; end: string; loading: boolean };
    month1: { start: string; end: string; loading: boolean };
    month2: { start: string; end: string; loading: boolean };
    month3: { start: string; end: string; loading: boolean };
    month4: { start: string; end: string; loading: boolean };
    month5: { start: string; end: string; loading: boolean };
    month6: { start: string; end: string; loading: boolean };
  };
}

// Configuration for paginated fetching
const CONFIG = {
  MAX_ROWS_CURRENT: 10000,    // Max rows to fetch for current period
  MAX_ROWS_HISTORICAL: 5000,  // Max rows to fetch for historical periods  
  PAGE_SIZE: 1000,            // Size of each pagination request
  COVERAGE_TARGET: 0.95       // Stop historical fetching when 95% coverage achieved
};

// Calculate date ranges for historical analysis
const getHistoricalDateRanges = () => {
  const now = new Date();
  const ranges = [];
  
  // Create consecutive 30-day periods: last 30 days, 31-60 days, 61-90 days, etc.
  for (let i = 0; i < 7; i++) {
    const endDate = new Date(now);
    endDate.setDate(endDate.getDate() - (1 + (30 * i))); // End dates: -1, -31, -61, etc.
    
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 29); // 30-day periods (inclusive)
    
    ranges.push({
      start: formatDateForAPI(startDate),
      end: formatDateForAPI(endDate),
      loading: false
    });
  }
  
  return {
    current: ranges[0],   // Last 30 days (today-30 to today-1)
    month1: ranges[1],    // 31-60 days ago
    month2: ranges[2],    // 61-90 days ago  
    month3: ranges[3],    // 91-120 days ago
    month4: ranges[4],    // 121-150 days ago
    month5: ranges[5],    // 151-180 days ago
    month6: ranges[6]     // 181-210 days ago
  };
};

// Fetch all rankings for a period using pagination
const fetchAllRankingsForPeriod = async (
  property: string,
  startDate: string,
  endDate: string,
  locationFilter: string = 'gbr',
  maxRows: number = CONFIG.MAX_ROWS_CURRENT,
  canonicalQueries?: Set<string>
): Promise<{ [query: string]: number }> => {
  const queryPositions: { [query: string]: number } = {};
  let startRow = 0;
  let foundCanonicalCount = 0;
  const targetCanonicalCount = canonicalQueries ? Math.floor(canonicalQueries.size * CONFIG.COVERAGE_TARGET) : 0;

  while (startRow < maxRows) {
    try {
      const request = {
        startDate,
        endDate,
        dimensions: ['query'],
        rowLimit: Math.min(CONFIG.PAGE_SIZE, maxRows - startRow),
        startRow,
        dimensionFilterGroups: locationFilter !== 'worldwide' ? [{
          filters: [{
            dimension: 'country',
            operator: 'equals',
            expression: locationFilter
          }]
        }] : undefined
      };

      const data = await fetchSearchConsoleData(property, request);
      
      if (!data || data.length === 0) {
        break; // No more data available
      }

      // Process this page of results
      for (const row of data) {
        queryPositions[row.query] = row.position;
        if (canonicalQueries && canonicalQueries.has(row.query)) {
          foundCanonicalCount++;
        }
      }

      startRow += data.length;

      // Early exit if we have good coverage of canonical queries
      if (canonicalQueries && foundCanonicalCount >= targetCanonicalCount) {
        console.log(`Early exit: Found ${foundCanonicalCount}/${canonicalQueries.size} canonical queries (${Math.round(foundCanonicalCount/canonicalQueries.size*100)}% coverage)`);
        break;
      }

      // If we got less than requested, we've reached the end
      if (data.length < CONFIG.PAGE_SIZE) {
        break;
      }

    } catch (error) {
      console.error('Error fetching rankings page:', error);
      break;
    }
  }

  return queryPositions;
};

// Main function to fetch all historical rankings with current-period anchoring
export const fetchHistoricalRankings = async (
  property: string,
  locationFilter: string = 'gbr',
  onProgress?: (progress: { period: string; completed: number; total: number }) => void
): Promise<HistoricalRankingData[]> => {
  const dateRanges = getHistoricalDateRanges();
  const periods = ['current', 'month1', 'month2', 'month3', 'month4', 'month5', 'month6'] as const;
  
  // Step 1: Fetch current period comprehensively to establish canonical keyword set
  console.log('Fetching current period with pagination...');
  const currentRange = dateRanges.current;
  const currentRankings = await fetchAllRankingsForPeriod(
    property,
    currentRange.start,
    currentRange.end,
    locationFilter,
    CONFIG.MAX_ROWS_CURRENT
  );
  
  const canonicalQueries = new Set(Object.keys(currentRankings));
  console.log(`Established canonical set: ${canonicalQueries.size} queries from current period`);
  
  if (onProgress) {
    onProgress({ period: 'current', completed: 1, total: periods.length });
  }

  // Step 2: Fetch historical periods with coverage targeting
  const historicalPromises = periods.slice(1).map(async (period, index) => {
    const range = dateRanges[period];
    console.log(`Fetching ${period} with coverage targeting...`);
    const rankings = await fetchAllRankingsForPeriod(
      property,
      range.start,
      range.end,
      locationFilter,
      CONFIG.MAX_ROWS_HISTORICAL,
      canonicalQueries
    );
    
    if (onProgress) {
      onProgress({ period, completed: index + 2, total: periods.length });
    }
    
    return { period, rankings };
  });

  const historicalResults = await Promise.all(historicalPromises);
  
  // Combine current with historical results
  const results = [
    { period: 'current', rankings: currentRankings },
    ...historicalResults
  ];
  
  // Combine results into historical data structure
  const allQueries = new Set<string>();
  const periodData: { [period: string]: { [query: string]: number } } = {};
  
  results.forEach(({ period, rankings }) => {
    periodData[period] = rankings;
    Object.keys(rankings).forEach(query => allQueries.add(query));
  });
  
  // Build historical ranking data for each query
  const historicalData: HistoricalRankingData[] = [];
  
  // Filter to only include canonical queries (those with current data) by default
  const queriesToProcess = canonicalQueries;
  console.log(`Processing ${queriesToProcess.size} canonical queries (queries with current data)`);
  
  queriesToProcess.forEach(query => {
    const positions: HistoricalPosition = {
      current: periodData.current[query] || null,
      month1: periodData.month1[query] || null,
      month2: periodData.month2[query] || null,
      month3: periodData.month3[query] || null,
      month4: periodData.month4[query] || null,
      month5: periodData.month5[query] || null,
      month6: periodData.month6[query] || null
    };
    
    // Calculate trend
    const currentPos = positions.current;
    const prevPos = positions.month1 || positions.month2 || positions.month3;
    
    let trend: 'up' | 'down' | 'stable' | 'new' = 'stable';
    let changeFromPrevious: number | null = null;
    
    if (currentPos && prevPos) {
      changeFromPrevious = prevPos - currentPos; // Positive = improvement (lower position number)
      if (changeFromPrevious > 2) {
        trend = 'up';
      } else if (changeFromPrevious < -2) {
        trend = 'down';
      } else {
        trend = 'stable';
      }
    } else if (currentPos && !prevPos) {
      trend = 'new';
    }
    
    historicalData.push({
      query,
      positions,
      trend,
      changeFromPrevious
    });
  });
  
  // Sort by current position (best rankings first)
  return historicalData.sort((a, b) => {
    if (!a.positions.current) return 1;
    if (!b.positions.current) return -1;
    return a.positions.current - b.positions.current;
  });
};

// Helper function to get trend icon
export const getTrendIcon = (trend: 'up' | 'down' | 'stable' | 'new') => {
  switch (trend) {
    case 'up': return '⬆';
    case 'down': return '⬇';
    case 'stable': return '⬌';
    case 'new': return '🌟';
    default: return '⬌';
  }
};

// Helper function to get trend color
export const getTrendColor = (trend: 'up' | 'down' | 'stable' | 'new') => {
  switch (trend) {
    case 'up': return 'text-green-600';
    case 'down': return 'text-red-600';
    case 'stable': return 'text-gray-600';
    case 'new': return 'text-blue-600';
    default: return 'text-gray-600';
  }
};

// Helper function to format position change
export const formatPositionChange = (change: number | null) => {
  if (change === null) return '';
  if (change > 0) return `+${change}`;
  return change.toString();
};